/**
 * state → html ของหน้าพนักงาน (ฟังก์ชันบริสุทธิ์ ไม่แตะ DOM — เทสต์ใน Node) · main.ts เป็นคนผูก DOM
 *
 * กฎเหล็กข้อ 1: ทุกหน้าจบที่บันทึกได้ — ส่งไม่ได้ = ภาพยังอยู่ในหน่วยความจำ กด "ลองส่งใหม่" ด้วย client_uuid เดิม (outbox ถาวรเป็น Sprint 4)
 * กฎเหล็กข้อ 4: สถานะบนจอมาจาก `tapeStatus()` เท่านั้น → pending/image · ไม่มีเส้นทาง bank
 * กฎเหล็กข้อ 7: state ของหน้านี้มีแค่พนักงานคนนี้ ร้านที่สังกัด และใบที่เพิ่งถ่าย — ไม่มียอดรวม โควตา หรือใบของคนอื่น
 * `renderScreen()` เป็นฟังก์ชันบริสุทธิ์ (state → html) เพื่อให้เทสต์ใน Node พิสูจน์สองข้อบนได้
 */
import type { IntakeResult } from "@pidyod/slip-intake";
import type { Me } from "./api.js";
import { Alert } from "./components/basics.js";
import { AllocationForm, CaptureSuccess, DuplicateNotice, SlipTape, tapeStatus, type KindId } from "./components/slip.js";
import { html } from "./h.js";

export interface Captured {
  blob: Blob;
  previewUrl: string;
  sha256: string;
  qrBankCode: string | null;
  transRef: string | null;
  qrOk: boolean;
  capturedAt: Date;
  clientUuid: string;
}

export type Screen =
  | { name: "loading" }
  | { name: "camera" }
  | { name: "confirm"; captured: Captured; kind: KindId; sending: boolean; error: string | null }
  | { name: "success"; captured: Captured; recordedAt: Date }
  | { name: "duplicate"; captured: Captured; firstSeen: { at: Date; shopName: string | null } | null };

export interface AppState {
  me: Me | null;
  shopIndex: number;
  screen: Screen;
  fatal: string | null;
}

export const shopOf = (s: AppState) => s.me?.shops[s.shopIndex] ?? { shop_id: "", shop_name: "—" };

/** state → html (ไม่แตะ DOM) */
export function renderScreen(s: AppState): string {
  const shop = shopOf(s);
  if (s.fatal) return html`<div class="sheet">${Alert({ tone: "danger", title: "เข้าใช้งานไม่ได้", body: s.fatal, action: { label: "ลองใหม่", action: "reload" } })}</div>`.s;
  switch (s.screen.name) {
    case "loading":
      return html`<div class="cam"></div>`.s;
    case "camera":
      return html`<div class="cam"></div>`.s; // mountCamera render เอง
    case "confirm": {
      const c = s.screen.captured;
      const status = tapeStatus({ aiAmount: null });
      const warnings = c.qrOk
        ? ["ยอดและชื่อผู้โอนจะอ่านจากภาพให้ · ตรวจเลขอ้างอิงให้ตรงกับในสลิปก่อนบันทึก"]
        : ["อ่าน QR ไม่ได้ จึงจะอ่านจากภาพแทน · ถ่ายใหม่ให้ QR ชัดจะกันซ้ำได้ดีกว่า"];
      return html`
        <div class="frozen">${c.previewUrl ? html`<img src="${c.previewUrl}" alt="ภาพสลิปที่ถ่ายไว้">` : null}<button type="button" class="retake" data-action="retake">ถ่ายใหม่</button></div>
        <div class="sheet">
          ${SlipTape({ data: { qrBankCode: c.qrBankCode, transRef: c.transRef, capturedAt: c.capturedAt, aiAmount: null }, status, warnings })}
          ${s.screen.error ? html`<div style="margin-top:12px">${Alert({ tone: "danger", title: "ส่งไม่สำเร็จ ภาพยังอยู่", body: s.screen.error })}</div>` : null}
          ${AllocationForm({ kind: s.screen.kind, sending: s.screen.sending, ctaLabel: s.screen.error ? "ลองส่งใหม่" : "บันทึกไว้ก่อน · ระบบจะตรวจให้", ctaVariant: "amber" })}
        </div>`.s;
    }
    case "success":
      return CaptureSuccess({ amount: null, time: s.screen.recordedAt, shopName: shop.shop_name, staffName: s.me?.staff_name ?? "" }).s;
    case "duplicate":
      return DuplicateNotice({ firstSeen: s.screen.firstSeen }).s;
  }
}

/** ผลจากท่อ → หน้าจอถัดไป — recorded/duplicate/failed ครบสามทาง ไม่มีทางไหนหลุดจากการบันทึก */
export function nextScreen(r: IntakeResult, captured: Captured, kind: KindId, currentShop: { shop_id: string; shop_name: string }): Screen {
  switch (r.outcome) {
    case "recorded":
      return { name: "success", captured, recordedAt: new Date(r.recorded_at) };
    case "duplicate":
      return {
        name: "duplicate", captured,
        firstSeen: r.existing ? { at: new Date(r.existing.recorded_at), shopName: r.existing.shop_id === currentShop.shop_id ? currentShop.shop_name : null } : null,
      };
    case "failed":
      return { name: "confirm", captured, kind, sending: false, error: r.note };
  }
}
